import { useState, type CSSProperties, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from './cn';

/**
 * Ant Design `Image` replacement — an <img> with an optional click-to-zoom preview
 * (fullscreen overlay) and a fallback source on error. `Image.PreviewGroup` renders
 * its children (group navigation is not needed by the app's usages).
 */
export interface ImageProps {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  /** antd allows `preview={false}` or `preview={{ src, mask }}`. */
  preview?: boolean | { src?: string; mask?: ReactNode; visible?: boolean };
  fallback?: string;
  placeholder?: ReactNode;
  onClick?: () => void;
}

function ImageBase({ src, alt = '', width, height, className, style, preview = true, fallback, onClick }: ImageProps) {
  const [open, setOpen] = useState(false);
  const [errored, setErrored] = useState(false);
  const shown = errored && fallback ? fallback : src;
  const previewOn = preview !== false;
  const previewSrc = typeof preview === 'object' && preview.src ? preview.src : src;
  return (
    <>
      <img
        src={shown}
        alt={alt}
        width={width}
        height={height}
        style={style}
        loading="lazy"
        onError={() => setErrored(true)}
        onClick={() => { onClick?.(); if (previewOn) setOpen(true); }}
        className={cn('inline-block object-cover', previewOn && 'cursor-zoom-in', className)}
      />
      {previewOn && open && (
        <div
          className="fixed inset-0 z-[var(--z-modal)] grid place-items-center bg-black/80 p-6 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <button type="button" aria-label="Close preview" className="absolute right-4 top-4 grid size-10 place-items-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25">
            <X className="size-5" />
          </button>
          <img src={previewSrc} alt={alt} className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-pop" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}

function PreviewGroup({ children }: { children?: ReactNode; preview?: unknown }) {
  return <>{children}</>;
}

export const Image = Object.assign(ImageBase, { PreviewGroup });
